// Non-modal, once-per-book invitation. Storage is optional and contains no personal data.
function createReadingInvitation(root, returnFocus) {
  const panel = root.querySelector('[data-reading-invitation]');
  const announcement = root.querySelector('[data-invitation-announcement]');
  const seen = new Set();
  const key = (id) => `woteLibraryInvitation:v1:${id}`;
  const hide = () => {
    if (!panel) return;
    const hadFocus = panel.contains(document.activeElement);
    panel.hidden = true;
    if (announcement) announcement.textContent = '';
    if (hadFocus) returnFocus();
  };
  panel?.querySelectorAll('[data-invitation-dismiss]').forEach((button) => button.addEventListener('click', hide));
  return {
    hide,
    isOpen: () => Boolean(panel && !panel.hidden),
    show(bookId, displayedPages) {
      if (!panel || !bookId) return;
      if (!displayedPages.includes(4)) { hide(); return; }
      if (seen.has(bookId)) return;
      try { if (window.localStorage.getItem(key(bookId)) === 'seen') return; } catch (_) { /* Private browsing: once per page session. */ }
      seen.add(bookId);
      try { window.localStorage.setItem(key(bookId), 'seen'); } catch (_) { /* Memory fallback already set. */ }
      panel.hidden = false;
      if (announcement) announcement.textContent = 'A personal invitation is available: explore tarot or find a ritual. You can dismiss it and keep reading.';
    }
  };
}

const libraryRoots = document.querySelectorAll('[data-living-library]');

libraryRoots.forEach((root) => initialiseLibrary(root).catch((error) => {
  console.error('Living Library could not initialise.', error);
  root.querySelector('[data-library-status]').textContent = 'The library could not be prepared.';
  root.querySelector('[data-library-direct-list]')?.removeAttribute('hidden');
}));

async function initialiseLibrary(root) {
  const core = await import(root.dataset.coreModule);
  const payload = JSON.parse(root.querySelector('[data-library-data]').textContent);
  const books = core.normalizeLibrary(payload);
  const shelves = root.querySelector('[data-library-shelves]');
  const collectionIndex = root.querySelector('[data-library-collections]');
  const collectionTabs = root.querySelector('[data-library-collection-tabs]');
  const reader = root.querySelector('[data-library-reader]');
  const status = root.querySelector('[data-library-status]');
  const loading = root.querySelector('[data-reader-loading]');
  const loadingCopy = loading.querySelector('p');
  const spread = root.querySelector('[data-reader-spread]');
  const errorPanel = root.querySelector('[data-reader-error]');
  const errorCopy = root.querySelector('[data-reader-error-copy]');
  const directLink = root.querySelector('[data-reader-direct]');
  const originalLink = root.querySelector('[data-reader-original]');
  const toc = root.querySelector('[data-reader-toc] ol');
  const tocPanel = root.querySelector('[data-reader-toc]');
  const contentsButton = root.querySelector('[data-reader-contents]');
  const title = root.querySelector('[data-reader-title]');
  const collection = root.querySelector('[data-reader-collection]');
  const readerAccess = root.querySelector('[data-reader-access]');
  const paywall = root.querySelector('[data-reader-paywall]');
  const paywallCopy = root.querySelector('[data-paywall-copy]');
  const purchaseLink = root.querySelector('[data-paywall-purchase]');
  const chapterLabel = root.querySelector('[data-reader-chapter]');
  const pageLabel = root.querySelector('[data-reader-page-label]');
  const zoomLabel = root.querySelector('[data-zoom-label]');
  const leftCanvas = root.querySelector('[data-reader-canvas="left"]');
  const rightCanvas = root.querySelector('[data-reader-canvas="right"]');
  const leftFigure = leftCanvas.closest('figure');
  const rightFigure = rightCanvas.closest('figure');
  const leftCaption = root.querySelector('[data-page-caption="left"]');
  const rightCaption = root.querySelector('[data-page-caption="right"]');
  const previous = root.querySelector('[data-page-prev]');
  const next = root.querySelector('[data-page-next]');
  const readingInvitation = createReadingInvitation(root, () => (next.disabled ? previous : next).focus({ preventScroll: true }));
  const skipAnimation = root.querySelector('[data-skip-animation]');
  const focusMode = root.querySelector('[data-reader-focus]');
  const directList = root.querySelector('[data-library-direct-list]');
  const storageKey = (bookId) => `woteLivingLibrary:${bookId}`;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let pdfModulePromise;
  let activeBook;
  let activeButton;
  let activeChapters = [];
  let activeContents = [];
  let activeDocs = new Map();
  const renderTasks = new Set();
  const textRenderTasks = new Set();
  let currentPage = 1;
  let totalPages = 0;
  let zoom = 1;
  let busy = false;
  let closing = false;
  let renderGeneration = 0;
  let failedChapter;
  let swipeStart = null;
  let activeCollection = 'all';
  let shelfCapacity = 0;
  let shelfResizeTimer;
  let cinemaSkipped = false;
  let cinemaAnimations = new Set();
  let cinemaLayer;
  let session = 0;
  let openingBook = false;
  let previewEndShown = false;

  const lastVisiblePage = () => isSinglePage() || currentPage === 1 || currentPage % 2 !== 0
    ? currentPage : Math.min(totalPages, currentPage + 1);
  const atPreviewEnd = () => totalPages > 0 && lastVisiblePage() >= totalPages;
  const hidePaywall = () => {
    if (paywall.hidden) return;
    paywall.hidden = true;
    next.focus({ preventScroll: true });
  };
  const showPaywall = () => {
    if (!reader.open || closing || spread.hidden || !activeBook?.previewOnly || !atPreviewEnd()) return;
    const completePages = activeBook.fullPageCount > totalPages ? activeBook.fullPageCount : null;
    const detail = completePages ? `${completePages}-page complete edition` : 'complete edition';
    paywallCopy.textContent = `You have read all ${totalPages} free preview pages of “${activeBook.title}”. The ${detail}${activeBook.priceLabel ? ` is ${activeBook.priceLabel}` : ''}.`;
    purchaseLink.href = activeBook.productUrl || '#';
    purchaseLink.hidden = !activeBook.productUrl;
    purchaseLink.textContent = activeBook.priceLabel ? `Get the complete book · ${activeBook.priceLabel}` : 'Get the complete book';
    previewEndShown = true;
    paywall.hidden = false;
    (purchaseLink.hidden ? root.querySelector('[data-paywall-dismiss]') : purchaseLink).focus({ preventScroll: true });
  };

  const readStorage = (key) => {
    try { return JSON.parse(window.localStorage.getItem(key) || 'null'); } catch (_) { return null; }
  };
  const writeStorage = (key, value) => {
    try { window.localStorage.setItem(key, JSON.stringify(value)); } catch (_) { /* private mode can deny storage */ }
  };
  const updateAddress = (bookId, chapterId, localPage) => {
    try {
      const url = new URL(window.location.href);
      if (bookId) url.searchParams.set('book', bookId); else url.searchParams.delete('book');
      if (chapterId) url.searchParams.set('chapter', chapterId); else url.searchParams.delete('chapter');
      if (chapterId && localPage) url.searchParams.set('page', String(localPage)); else url.searchParams.delete('page');
      window.history.replaceState({}, '', url);
    } catch (_) { /* address enhancement is optional */ }
  };

  const motionlessFromStorage = (() => {
    try { return window.localStorage.getItem('woteLivingLibraryMotionless') === 'true'; } catch (_) { return false; }
  })();
  if (motionlessFromStorage) {
    root.classList.add('is-motionless');
    skipAnimation.setAttribute('aria-pressed', 'true');
  }

  const shouldAnimate = () => typeof reader.animate === 'function' && !root.classList.contains('is-motionless') && !prefersReducedMotion.matches;
  const isSinglePage = () => window.matchMedia('(max-width: 900px)').matches;

  const syncTocAccessibility = ({ focusPanel = false } = {}) => {
    const visible = !reader.classList.contains('is-focus-mode') && (!isSinglePage() || reader.classList.contains('is-toc-open'));
    // Move focus out before hiding the panel from accessibility APIs.
    if (!visible && tocPanel.contains(document.activeElement)) contentsButton.focus({ preventScroll: true });
    tocPanel.inert = !visible;
    tocPanel.setAttribute('aria-hidden', String(!visible));
    contentsButton.setAttribute('aria-expanded', String(visible));
    if (visible && focusPanel) {
      const buttons = [...tocPanel.querySelectorAll('button')].filter((button) => !button.closest('[hidden]') && !button.disabled);
      const target = buttons.find((button) => button.getAttribute('aria-current') === 'location') || buttons[0] || tocPanel;
      target.focus({ preventScroll: true });
    }
  };
  syncTocAccessibility();

  const createText = (tag, className, value) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    node.textContent = value;
    return node;
  };

  const stableNumber = (value) => [...String(value)].reduce((total, character) => ((total * 31) + character.charCodeAt(0)) >>> 0, 7);

  const coverPreparations = new Map();
  const prepareCover = (book) => {
    if (!book.cover.image || !book.cover.autoColor) return Promise.resolve();
    if (!coverPreparations.has(book.id)) {
      coverPreparations.set(book.id, new Promise((resolve) => {
        const image = new Image();
        let finished = false;
        const finish = () => { finished = true; clearTimeout(timeout); resolve(); };
        const timeout = setTimeout(finish, 2500);
        image.crossOrigin = 'anonymous';
        image.onload = () => {
          if (finished) return;
          try {
            const canvas = document.createElement('canvas');
            canvas.width = canvas.height = 48;
            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            ctx.drawImage(image, 0, 0, 48, 48);
            book.cover.color = core.coverTone(ctx.getImageData(0, 0, 48, 48).data, book.cover.color);
          } catch (_) { /* Cross-origin artwork keeps its configured fallback. */ }
          finish();
        };
        image.onerror = finish;
        image.src = book.cover.image;
      }));
    }
    return coverPreparations.get(book.id);
  };

  const createBookButton = (book) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'll-book';
    if (book.previewOnly) button.classList.add('ll-book--preview');
    button.dataset.bookId = book.id;
    const variation = stableNumber(book.id);
    button.style.setProperty('--cover', book.cover.color);
    button.style.setProperty('--accent', book.cover.accent);
    button.style.setProperty('--book-width', `${82 + (variation % 24)}px`);
    button.style.setProperty('--book-height', `${238 + (variation % 67)}px`);
    button.style.setProperty('--book-lean', `${((variation % 7) - 3) * .45}deg`);
    button.setAttribute('aria-label', `Open ${book.title}. ${book.previewOnly ? 'Free preview; complete book available for purchase. ' : ''}${book.description}`);
    button.setAttribute('aria-pressed', 'false');
    const model = document.createElement('span');
    model.className = 'll-volume';
    model.setAttribute('aria-hidden', 'true');
    const cover = document.createElement('span');
    cover.className = 'll-volume__front';
    if (book.cover.image) {
      model.classList.add('ll-volume--illustrated');
      const image = document.createElement('span');
      image.className = 'll-volume__art';
      image.style.backgroundImage = `url("${book.cover.image.replaceAll('"', '%22')}")`;
      cover.append(image);
    }
    if (!book.cover.image) {
      cover.append(createText('span', 'll-volume__imprint', 'WHISPERS OF THE EAST'));
      cover.append(createText('span', 'll-volume__name', book.title));
      cover.append(createText('span', 'll-volume__emblem', '☾'));
      cover.append(createText('span', 'll-volume__collection', book.collection));
    }
    if (book.previewOnly) cover.append(createText('span', 'll-volume__preview', 'Preview'));
    const spine = createText('span', 'll-volume__spine', '');
    spine.append(createText('span', '', book.title));
    const endpaper = createText('span', 'll-volume__paper', '');
    endpaper.append(createText('span', 'll-volume__paper-title', book.title));
    endpaper.append(createText('span', 'll-volume__paper-mark', 'Whispers of the East'));
    model.append(createText('span', 'll-volume__back', ''), endpaper, createText('span', 'll-volume__edge', ''), spine, cover);
    button.append(model);
    const info = document.createElement('span');
    info.className = 'll-book__info';
    info.append(createText('span', 'll-book__collection', book.collection));
    info.append(createText('strong', '', book.title));
    if (book.previewOnly) info.append(createText('span', 'll-book__preview-note', `Free preview · Full book${book.priceLabel ? ` ${book.priceLabel}` : ''}`));
    info.append(createText('small', '', book.description));
    button.append(info);
    button.addEventListener('click', () => openBook(book, button));
    prepareCover(book).then(() => button.style.setProperty('--cover', book.cover.color));
    return button;
  };

  const restoreShelfFocus = (bookId) => {
    const button = bookId && shelves.querySelector(`[data-book-id="${CSS.escape(bookId)}"]`);
    if (!button) return false;
    button.setAttribute('aria-pressed', 'false');
    button.focus({ preventScroll: true });
    return true;
  };

  const visibleBooks = () => activeCollection === 'all' ? books : books.filter((book) => book.collection === activeCollection);

  const renderShelves = (force = false) => {
    const measuredWidth = shelves.clientWidth || root.clientWidth;
    const nextCapacity = window.matchMedia('(max-width: 620px)').matches
      ? Math.max(2, Math.min(4, Math.floor((measuredWidth - 40) / 86)))
      : core.calculateShelfCapacity(measuredWidth, 126, 4, 8);
    if (!force && nextCapacity === shelfCapacity && shelves.children.length) return;
    const focusedBookId = shelves.contains(document.activeElement) ? document.activeElement.closest('[data-book-id]')?.dataset.bookId : null;
    shelfCapacity = nextCapacity;
    const rows = core.chunkBooks(visibleBooks(), shelfCapacity);
    root.style.setProperty('--shelf-count', String(Math.max(1, rows.length)));
    shelves.replaceChildren();
    rows.forEach((row, rowIndex) => {
      const shelf = document.createElement('div');
      shelf.className = 'll-shelf';
      shelf.dataset.shelf = String(rowIndex + 1);
      const label = createText('span', 'll-shelf__label', `Shelf ${String(rowIndex + 1).padStart(2, '0')}`);
      label.append(createText('small', '', `${row.length} ${row.length === 1 ? 'volume' : 'volumes'}`));
      shelf.append(label);
      row.forEach((book, index) => {
        const button = createBookButton(book);
        button.style.setProperty('--arrival', `${index * 45}ms`);
        shelf.append(button);
      });
      shelves.append(shelf);
    });
    if (!rows.length) shelves.append(createText('p', 'll-loading-copy', 'No volume belongs to this collection yet.'));
    status.textContent = `${visibleBooks().length} ${visibleBooks().length === 1 ? 'volume' : 'volumes'} · ${rows.length} ${rows.length === 1 ? 'shelf' : 'shelves'}.`;
    // Resizing after the modal closes may replace the button that just regained focus.
    if (focusedBookId) restoreShelfFocus(focusedBookId);
  };

  const collections = [...new Set(books.map((book) => book.collection).filter(Boolean))];
  if (collections.length) {
    collectionIndex.hidden = false;
    ['all', ...collections].forEach((name) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = name === 'all' ? 'All volumes' : name;
      button.dataset.collection = name;
      button.setAttribute('aria-pressed', String(name === 'all'));
      button.setAttribute('aria-controls', shelves.id);
      button.addEventListener('click', () => {
        activeCollection = name;
        collectionTabs.querySelectorAll('button').forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
        renderShelves(true);
      });
      collectionTabs.append(button);
    });
  }

  renderShelves(true);

  if (!books.length) {
    shelves.append(createText('p', 'll-loading-copy', 'No published volume is available yet.'));
    status.textContent = 'No published books.';
  } else {
    status.textContent = `${books.length} ${books.length === 1 ? 'volume' : 'volumes'} ready to open.`;
  }

  const directItems = directList.querySelector('ul');
  books.forEach((book) => book.chapters.forEach((chapter) => {
    const item = document.createElement('li');
    const link = document.createElement('a');
    link.href = chapter.url || '#';
    link.target = '_blank';
    link.rel = 'noopener';
    link.textContent = `${book.title} — ${chapter.title}${book.previewOnly ? ' (free preview)' : ''}`;
    item.append(link);
    directItems.append(item);
  }));

  const getPdfModule = async () => {
    if (!pdfModulePromise) {
      pdfModulePromise = import(root.dataset.pdfModule).then((module) => {
        module.GlobalWorkerOptions.workerSrc = root.dataset.pdfWorker;
        return module;
      });
    }
    return pdfModulePromise;
  };

  const clearDocuments = async () => {
    textRenderTasks.forEach((task) => task.cancel());
    textRenderTasks.clear();
    const docs = [...activeDocs.values()];
    activeDocs = new Map();
    await Promise.all(docs.map((doc) => doc.destroy?.().catch(() => {})));
  };

  const animateCinema = async (node, frames, duration) => {
    const animation = node.animate(frames, {duration: cinemaSkipped ? 1 : duration, easing: 'cubic-bezier(.24,.68,.22,1)', fill: 'forwards'});
    cinemaAnimations.add(animation);
    await animation.finished.catch(() => {});
    cinemaAnimations.delete(animation);
  };

  const finishCinema = () => {
    cinemaSkipped = true;
    cinemaAnimations.forEach((animation) => { try { animation.finish(); } catch (_) { /* detached animation */ } });
  };

  const travelBook = async (button, returning = false) => {
    if (!button || !shouldAnimate()) return;
    cinemaSkipped = false;
    const source = button.getBoundingClientRect();
    const bookHeight = button.offsetHeight;
    const bookWidth = bookHeight * .68;
    const sourceX = source.left + source.width / 2 - bookWidth / 2;
    const sourceY = source.top;
    const scale = Math.min(1.7, innerWidth * .40 / bookWidth, innerHeight * .48 / bookHeight);
    const targetX = innerWidth * .54 - bookWidth / 2;
    const targetY = innerHeight * .50 - bookHeight / 2;
    cinemaLayer = createText('div', 'll-cinema', '');
    const atmosphere = createText('div', 'll-cinema__atmosphere', '');
    const shadow = createText('div', 'll-cinema__shadow', '');
    const caption = createText('div', 'll-cinema__caption', '');
    caption.append(createText('span', '', returning ? 'UNTIL NEXT TIME' : 'FROM THE SHELF TO YOUR HANDS'));
    caption.append(createText('strong', '', activeBook.title));
    const skip = createText('button', 'll-cinema__skip', 'Skip animation');
    skip.type = 'button';
    skip.addEventListener('click', finishCinema);
    const traveller = createText('div', 'll-cinema__book', '');
    traveller.setAttribute('aria-hidden', 'true');
    traveller.style.cssText = `width:${bookWidth}px;height:${bookHeight}px;--cover:${activeBook.cover.color};--accent:${activeBook.cover.accent};`;
    const shelfModel = button.querySelector('.ll-volume');
    const shelfTransform = getComputedStyle(shelfModel).transform;
    const model = shelfModel.cloneNode(true);
    traveller.append(model);
    cinemaLayer.append(atmosphere, shadow, traveller, caption, skip);
    reader.append(cinemaLayer);
    reader.classList.add('is-cinematic');
    skip.focus();
    const sourceTransform = `translate3d(${sourceX}px,${sourceY}px,0) rotateX(0deg) rotateZ(0deg) scale(1)`;
    const nearTransform = `translate3d(${targetX - 20}px,${targetY - 30}px,0) rotateX(7deg) rotateZ(-5deg) scale(${scale * 1.05})`;
    const tableTransform = `translate3d(${targetX}px,${targetY}px,0) rotateX(15deg) rotateZ(-3deg) scale(${scale})`;
    const front = model.querySelector('.ll-volume__front');
    if (!returning) {
      await Promise.all([
        animateCinema(atmosphere, [{opacity:0},{opacity:1}], 650),
        animateCinema(caption, [{opacity:0,transform:'translateY(12px)'},{opacity:1,transform:'none'}], 620),
        animateCinema(traveller, [{transform:sourceTransform},{transform:`translate3d(${sourceX}px,${sourceY-24}px,0) rotateX(0deg) rotateZ(-2deg) scale(1.1)`,offset:.22},{transform:nearTransform,offset:.76},{transform:tableTransform}], 760),
        animateCinema(model, [{transform:shelfTransform},{transform:'rotateY(44deg)',offset:.22},{transform:'rotateY(12deg)',offset:.76},{transform:'rotateY(0deg)'}], 760),
        animateCinema(shadow, [{opacity:0,transform:'translate(-50%,-50%) scale(.65)'},{opacity:.36,transform:'translate(-50%,-50%) scale(1.08)',offset:.65},{opacity:.72,transform:'translate(-50%,-50%) scale(1)'}], 760)
      ]);
      await animateCinema(front, [{transform:'translateZ(18px) rotateY(0deg)'},{transform:'translateZ(18px) rotateY(-154deg)'}], 420);
      reader.classList.remove('is-cinematic');
      await animateCinema(cinemaLayer, [{opacity:1},{opacity:0}], 170);
    } else {
      atmosphere.style.opacity = '1';
      traveller.style.transform = tableTransform;
      model.style.transform = 'rotateY(0deg)';
      await animateCinema(front, [{transform:'translateZ(18px) rotateY(-154deg)'},{transform:'translateZ(18px) rotateY(0deg)'}], 400);
      await Promise.all([
        animateCinema(traveller, [{transform:tableTransform},{transform:nearTransform,offset:.25},{transform:sourceTransform}], 650),
        animateCinema(model, [{transform:'rotateY(0deg)'},{transform:shelfTransform}], 650),
        animateCinema(atmosphere, [{opacity:1},{opacity:0}], 650),
        animateCinema(caption, [{opacity:1},{opacity:0}], 400)
      ]);
    }
    cinemaLayer.remove();
    cinemaLayer = null;
    reader.classList.remove('is-cinematic');
  };

  const setReaderState = (state) => {
    loading.hidden = state !== 'loading';
    errorPanel.hidden = state !== 'error';
    spread.hidden = state !== 'ready';
    if (state !== 'error') directList.hidden = true;
  };

  const showError = (problem, chapter) => {
    if (closing || !reader.open || !activeBook) return;
    failedChapter = chapter;
    setReaderState('error');
    errorCopy.textContent = `${chapter?.title || 'The selected chapter'} could not be loaded. ${problem?.message || 'Check its Shopify file reference.'}`;
    directLink.href = chapter?.url || '#';
    directLink.hidden = !chapter?.url;
    updateOriginalLink(chapter);
    directList.hidden = false;
    status.textContent = 'A chapter file is missing or unavailable.';
  };

  const updateOriginalLink = (chapter) => {
    if (!originalLink) return;
    originalLink.hidden = !chapter?.url;
    originalLink.href = chapter?.url || '#';
    originalLink.firstChild.textContent = activeBook?.previewOnly ? 'Preview PDF ' : 'Original PDF ';
    originalLink.setAttribute('aria-label', `Open ${activeBook?.previewOnly ? 'preview' : 'original'} PDF: ${chapter?.title || activeBook?.title || 'selected book'} (new tab)`);
  };

  const buildToc = () => {
    toc.replaceChildren();
    const appendEntry = (entry, parentList) => {
      const index = entry.index;
      const item = document.createElement('li');
      item.className = entry.depth ? 'll-toc__subchapter' : 'll-toc__chapter';
      const row = document.createElement('div');
      row.className = 'll-toc__row';
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.contentsIndex = String(index);
      button.append(document.createTextNode(entry.title));
      button.append(createText('small', '', `Page ${entry.page}`));
      button.addEventListener('click', () => {
        currentPage = entry.page;
        const targetChapter = activeChapters.find((chapter) => chapter.id === entry.chapterId);
        updateAddress(activeBook?.id, entry.chapterId, entry.page - targetChapter.startPage + 1);
        reader.classList.remove('is-toc-open');
        syncTocAccessibility();
        renderSpread();
      });
      row.append(button);
      item.append(row);
      if (entry.children.length) {
        const children = document.createElement('ol');
        children.id = `ll-contents-${root.closest('.shopify-section')?.id || 'library'}-${index}`;
        children.hidden = true;
        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'll-toc__toggle';
        toggle.textContent = '⌄';
        toggle.setAttribute('aria-expanded', 'false');
        toggle.setAttribute('aria-controls', children.id);
        toggle.setAttribute('aria-label', `Show subsections: ${entry.title}`);
        toggle.addEventListener('click', () => {
          const expanded = toggle.getAttribute('aria-expanded') !== 'true';
          toggle.setAttribute('aria-expanded', String(expanded));
          toggle.setAttribute('aria-label', `${expanded ? 'Hide' : 'Show'} subsections: ${entry.title}`);
          children.hidden = !expanded;
        });
        row.append(toggle);
        entry.children.forEach((child) => appendEntry(child, children));
        item.append(children);
      }
      parentList.append(item);
    };
    core.buildContentsTree(activeContents).forEach((entry) => appendEntry(entry, toc));
  };

  const savedPosition = () => {
    const position = core.globalToLocal(activeChapters, currentPage);
    if (!activeBook || !position) return;
    writeStorage(storageKey(activeBook.id), {
      chapterId: position.chapter.id,
      localPage: position.localPage,
      globalPage: position.globalPage,
      updatedAt: activeBook.updatedAt || ''
    });
  };

  const loadBook = async (book) => {
    const loadingSession = session;
    activeContents = [];
    activeChapters = [];
    totalPages = 0;
    toc.replaceChildren();
    setReaderState('loading');
    loadingCopy.textContent = 'Opening the volume and counting its pages…';
    errorPanel.hidden = true;
    await clearDocuments();
    const pageCounts = {};
    try {
      const pdfjs = await getPdfModule();
      if (loadingSession !== session) return;
      for (const chapter of book.chapters) {
        failedChapter = chapter;
        loadingCopy.textContent = `Preparing “${chapter.title}”…`;
        if (!chapter.url) throw new Error('No PDF file is attached.');
        const task = pdfjs.getDocument({ url: chapter.url });
        const document = await task.promise;
        if (loadingSession !== session) { await document.destroy(); return; }
        activeDocs.set(chapter.id, document);
        pageCounts[chapter.id] = document.numPages;
      }
      activeChapters = core.paginateChapters(book.chapters, pageCounts);
      totalPages = activeChapters.reduce((sum, chapter) => sum + chapter.pageCount, 0);
      if (!totalPages) throw new Error('The volume contains no readable page.');
      readerAccess.hidden = !book.previewOnly;
      if (book.previewOnly) readerAccess.textContent = `Free preview · ${totalPages} of ${Math.max(book.fullPageCount || 0, totalPages)} pages`;
      for (const chapter of activeChapters) {
        const outline = await core.readPdfContents(activeDocs.get(chapter.id), chapter);
        if (loadingSession !== session) return;
        activeContents.push(...(outline.length ? outline : [{ title: chapter.title, chapterId: chapter.id, depth: 0, page: chapter.startPage }]));
      }
      buildToc();
      currentPage = core.resolveSavedPosition(activeChapters, readStorage(storageKey(book.id)));
      zoom = 1;
      zoomLabel.value = '100%';
      zoomLabel.textContent = '100%';
      setReaderState('ready');
      status.textContent = `${book.title} is open at page ${currentPage}.`;
      await renderSpread();
    } catch (problem) {
      if (loadingSession !== session) return;
      showError(problem, failedChapter);
    }
  };

  const renderPdfLinks = async (page, pdf, chapter, surface, generation) => {
    let annotations;
    try { annotations = await page.getAnnotations({ intent: 'display' }); } catch (_) { return; }
    if (generation !== renderGeneration) return;
    const links = annotations.filter((item) => item.subtype === 'Link');
    if (!links.length) return;
    let textItems = [];
    try { textItems = (await page.getTextContent()).items; } catch (_) { /* URL/page labels remain available. */ }
    const viewport = page.getViewport({ scale: 1 });
    const layer = document.createElement('div');
    layer.className = 'll-pdf-links';
    for (const annotation of links) {
      const box = core.pdfLinkBox(annotation.rect, viewport);
      if (!box) continue;
      const href = core.safePdfLink(annotation.url);
      const localPage = href ? null : await core.pdfDestinationPage(pdf, annotation.dest);
      if (!href && !localPage) continue;
      if (generation !== renderGeneration) return;
      const element = document.createElement(href ? 'a' : 'button');
      element.className = 'll-pdf-link';
      Object.entries(box).forEach(([name, value]) => { element.style[name] = `${value}%`; });
      const [x1, y1, x2, y2] = annotation.rect;
      const text = textItems.filter((item) => item.str && item.transform &&
        item.transform[4] + item.width > Math.min(x1, x2) && item.transform[4] < Math.max(x1, x2) &&
        item.transform[5] + item.height > Math.min(y1, y2) && item.transform[5] < Math.max(y1, y2))
        .map((item) => item.str).join(' ').trim();
      const label = href ? `${text || href} (opens in a new tab)` : `${text || 'Go to page'} — page ${chapter.startPage + localPage - 1}`;
      element.setAttribute('aria-label', label);
      element.title = label;
      if (href) {
        element.href = href;
        element.target = '_blank';
        element.rel = 'noopener noreferrer';
      } else {
        element.type = 'button';
        element.addEventListener('click', () => {
          currentPage = chapter.startPage + localPage - 1;
          updateAddress(activeBook?.id, chapter.id, localPage);
          renderSpread();
          root.querySelector('[data-reader-close]').focus({ preventScroll: true });
        });
      }
      layer.append(element);
    }
    if (generation === renderGeneration) surface.append(layer);
  };

  const renderPdfPage = async (globalPage, canvas, caption, generation, sheet) => {
    const position = core.globalToLocal(activeChapters, globalPage);
    if (!position || generation !== renderGeneration) return;
    const pdf = activeDocs.get(position.chapter.id);
    if (!pdf) throw new Error(`Document not loaded for ${position.chapter.title}.`);
    const page = await pdf.getPage(position.localPage);
    if (generation !== renderGeneration) return;
    const base = page.getViewport({ scale: 1 });
    const contained = core.containPdfPage(base.width, base.height, sheet);
    const cssScale = contained.scale;
    const outputScale = Math.min(2, window.devicePixelRatio || 1);
    const viewport = page.getViewport({ scale: cssScale * outputScale });
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    let surface = canvas.closest('.ll-page__surface');
    if (!surface) {
      surface = document.createElement('div');
      surface.className = 'll-page__surface';
      canvas.before(surface);
      surface.append(canvas);
    }
    surface.style.width = `${contained.width}px`;
    surface.style.height = `${contained.height}px`;
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    const renderTask = page.render({ canvasContext: canvas.getContext('2d', { alpha: false }), viewport });
    renderTasks.add(renderTask);
    try { await renderTask.promise; } finally { renderTasks.delete(renderTask); }
    if (generation !== renderGeneration) return;
    await Promise.all([
      renderPdfText(page, surface, cssScale, generation),
      renderPdfLinks(page, pdf, position.chapter, surface, generation)
    ]);
    if (generation !== renderGeneration) return;
    caption.textContent = `Page ${position.globalPage} · ${position.chapter.title}`;
  };

  // PDF.js supplies the real text geometry; no OCR or reconstructed page content.
  // Keep this layer below the existing annotation links, on the same page surface.
  const renderPdfText = async (page, surface, scale, generation) => {
    let task;
    let layer;
    try {
      const pdfjs = await getPdfModule();
      const textContent = await page.getTextContent();
      if (generation !== renderGeneration || !pdfjs.TextLayer) return;
      layer = document.createElement('div');
      layer.className = 'll-pdf-text';
      layer.style.setProperty('--total-scale-factor', scale);
      surface.append(layer);
      task = new pdfjs.TextLayer({ textContentSource: textContent, container: layer, viewport: page.getViewport({ scale }) });
      // Exact surface dimensions also work where CSS round() is unavailable.
      layer.style.width = surface.style.width;
      layer.style.height = surface.style.height;
      textRenderTasks.add(task);
      await task.render();
      if (generation !== renderGeneration) layer.remove();
    } catch (_) {
      layer?.remove(); // Scanned/unusual PDFs still have their canvas and original link.
    } finally {
      if (task) textRenderTasks.delete(task);
    }
  };

  const renderSpread = async () => {
    if (closing || !reader.open || !activeBook || !activeChapters.length || !totalPages) return;
    const renderingSession = session;
    syncTocAccessibility();
    reader.style.setProperty('--ll-reader-header-height', `${reader.querySelector('.ll-reader__bar').offsetHeight}px`);
    reader.style.setProperty('--ll-reader-footer-height', `${reader.querySelector('.ll-reader__controls').offsetHeight}px`);
    const generation = ++renderGeneration;
    textRenderTasks.forEach((task) => task.cancel());
    textRenderTasks.clear();
    spread.querySelectorAll('.ll-pdf-links,.ll-pdf-text').forEach((layer) => layer.remove());
    const pendingRenders = [...renderTasks];
    pendingRenders.forEach((task) => task.cancel());
    await Promise.allSettled(pendingRenders.map((task) => task.promise));
    if (generation !== renderGeneration || renderingSession !== session || closing || !reader.open) return;
    const single = isSinglePage();
    const pagesArea = root.querySelector('[data-reader-pages]');
    const areaStyle = getComputedStyle(pagesArea);
    const spreadStyle = getComputedStyle(spread);
    const columns = single ? 1 : 2;
    const binding = single ? 0 : 12;
    const borderX = parseFloat(spreadStyle.borderLeftWidth) + parseFloat(spreadStyle.borderRightWidth);
    const borderY = parseFloat(spreadStyle.borderTopWidth) + parseFloat(spreadStyle.borderBottomWidth);
    const availableWidth = pagesArea.clientWidth - parseFloat(areaStyle.paddingLeft) - parseFloat(areaStyle.paddingRight);
    const availableHeight = pagesArea.clientHeight - parseFloat(areaStyle.paddingTop) - parseFloat(areaStyle.paddingBottom);
    const sheet = core.a4PageLayout((availableWidth - borderX - binding) / columns, availableHeight - borderY, zoom);
    spread.style.width = `${sheet.width * columns + binding + borderX}px`;
    spread.style.setProperty('--ll-page-width', `${sheet.width}px`);
    spread.style.setProperty('--ll-page-height', `${sheet.height}px`);
    let leftPage = null;
    let rightPage = currentPage;
    if (!single) {
      if (currentPage === 1) {
        rightPage = 1;
      } else {
        leftPage = currentPage % 2 === 0 ? currentPage : currentPage - 1;
        rightPage = leftPage + 1 <= totalPages ? leftPage + 1 : null;
      }
    }
    leftFigure.hidden = single || leftPage === null;
    rightFigure.hidden = rightPage === null;
    try {
      const jobs = [];
      if (leftPage) jobs.push(renderPdfPage(leftPage, leftCanvas, leftCaption, generation, sheet));
      if (rightPage) jobs.push(renderPdfPage(rightPage, rightCanvas, rightCaption, generation, sheet));
      await Promise.all(jobs);
      if (generation !== renderGeneration || renderingSession !== session || closing || !reader.open) return;
      const position = core.globalToLocal(activeChapters, currentPage);
      updateOriginalLink(position.chapter);
      const currentEntry = core.contentsAtPage(activeContents, currentPage);
      chapterLabel.textContent = currentEntry?.title || position.chapter.title;
      pageLabel.textContent = single || currentPage === 1 || rightPage === null ? `Page ${currentPage} of ${totalPages}` : `Pages ${leftPage}–${rightPage} of ${totalPages}`;
      previous.disabled = currentPage <= 1;
      next.disabled = activeBook.previewOnly ? false : single ? currentPage >= totalPages : (rightPage || currentPage) >= totalPages;
      next.textContent = activeBook.previewOnly && atPreviewEnd() ? 'Unlock full book →' : 'Next →';
      next.setAttribute('aria-label', activeBook.previewOnly && atPreviewEnd() ? 'Unlock the complete book' : 'Next page');
      toc.querySelectorAll('[data-contents-index]').forEach((button) => {
        const isCurrent = activeContents[Number(button.dataset.contentsIndex)] === currentEntry;
        button.classList.toggle('is-active', isCurrent);
        if (isCurrent) button.setAttribute('aria-current', 'location');
        else button.removeAttribute('aria-current');
      });
      toc.querySelectorAll('li').forEach((item) => item.classList.toggle('contains-current', Boolean(item.querySelector('[aria-current="location"]'))));
      savedPosition();
      setReaderState('ready');
      if (activeBook.previewOnly) {
        readingInvitation.hide();
        if (!atPreviewEnd()) {
          previewEndShown = false;
          if (!paywall.hidden) hidePaywall();
        } else if (!openingBook && !previewEndShown) showPaywall();
      } else readingInvitation.show(activeBook.id, [leftPage, rightPage]);
    } catch (problem) {
      if (generation !== renderGeneration || renderingSession !== session || closing || !reader.open || problem?.name === 'RenderingCancelledException' || problem?.name === 'AbortException') return;
      showError(problem, core.globalToLocal(activeChapters, currentPage)?.chapter);
      readingInvitation.hide();
    }
  };

  const turnPage = (direction) => {
    if (!totalPages) return;
    if (direction > 0 && activeBook?.previewOnly && atPreviewEnd()) { showPaywall(); return; }
    reader.classList.remove('is-turning-forward', 'is-turning-back');
    reader.classList.add(direction > 0 ? 'is-turning-forward' : 'is-turning-back');
    const step = isSinglePage() ? 1 : 2;
    if (direction > 0) currentPage = Math.min(totalPages, currentPage === 1 && !isSinglePage() ? 2 : currentPage + step);
    else currentPage = Math.max(1, currentPage - step);
    renderSpread();
    window.setTimeout(() => reader.classList.remove('is-turning-forward', 'is-turning-back'), 280);
  };

  const openBook = async (book, button) => {
    if (busy || closing || reader.open) return;
    const requestedLocalPage = new URL(window.location.href).searchParams.get('page');
    const requestedChapterId = (() => {
      try {
        const url = new URL(window.location.href);
        return url.searchParams.get('book') === book.id ? url.searchParams.get('chapter') : null;
      } catch (_) { return null; }
    })();
    busy = true;
    openingBook = true;
    previewEndShown = false;
    await prepareCover(book);
    session += 1;
    const openingSession = session;
    activeBook = book;
    paywall.hidden = true;
    readerAccess.hidden = !book.previewOnly;
    updateOriginalLink(book.chapters[0]);
    activeButton = button;
    button.setAttribute('aria-pressed', 'true');
    root.classList.add('is-book-active');
    updateAddress(book.id, null);
    status.textContent = `${book.title} is moving to the reading table.`;
    title.textContent = book.title;
    collection.textContent = book.collection;
    reader.style.setProperty('--cover', book.cover.color);
    reader.style.setProperty('--accent', book.cover.accent);
    if (shouldAnimate()) reader.classList.add('is-cinematic');
    reader.showModal();
    syncTocAccessibility();
    document.documentElement.style.overflow = 'hidden';
    const loadingBook = loadBook(book);
    await travelBook(button);
    if (openingSession !== session) return;
    root.querySelector('[data-reader-close]').focus();
    busy = false;
    await loadingBook;
    if (openingSession !== session) return;
    const linkedChapter = activeChapters.find((chapter) => chapter.id === requestedChapterId);
    if (linkedChapter) {
      currentPage = linkedChapter.startPage + Math.min(linkedChapter.pageCount, Math.max(1, Number(requestedLocalPage) || 1)) - 1;
      updateAddress(book.id, linkedChapter.id, currentPage - linkedChapter.startPage + 1);
      await renderSpread();
    }
    openingBook = false;
    busy = false;
    if (book.previewOnly && atPreviewEnd() && !previewEndShown) showPaywall();
  };

  const closeBook = async () => {
    if (!reader.open || closing) return;
    if (cinemaLayer) { finishCinema(); return; }
    closing = true;
    openingBook = false;
    paywall.hidden = true;
    readingInvitation.hide();
    session += 1;
    renderGeneration += 1;
    savedPosition();
    status.textContent = `${activeBook?.title || 'The book'} is returning to its shelf.`;
    await clearDocuments();
    await travelBook(activeButton, true);
    reader.close();
    document.documentElement.style.overflow = '';
    root.classList.remove('is-book-active');
    updateAddress(null, null);
    reader.classList.remove('is-toc-open');
    syncTocAccessibility();
    renderShelves();
    restoreShelfFocus(activeBook?.id);
    status.textContent = 'Library ready.';
    activeBook = null;
    activeButton = null;
    activeChapters = [];
    totalPages = 0;
    busy = false;
    closing = false;
  };

  root.querySelector('[data-reader-close]').addEventListener('click', closeBook);
  contentsButton.addEventListener('click', () => {
    if (reader.classList.contains('is-focus-mode')) {
      reader.classList.remove('is-focus-mode');
      focusMode.setAttribute('aria-pressed', 'false');
      focusMode.textContent = 'Reading mode';
      window.setTimeout(renderSpread, 40);
    }
    if (isSinglePage()) reader.classList.toggle('is-toc-open');
    syncTocAccessibility({ focusPanel: true });
  });
  reader.addEventListener('cancel', (event) => {
    event.preventDefault();
    if (!paywall.hidden) hidePaywall();
    else if (readingInvitation.isOpen()) readingInvitation.hide();
    else closeBook();
  });
  root.querySelector('[data-paywall-dismiss]').addEventListener('click', hidePaywall);
  previous.addEventListener('click', () => turnPage(-1));
  next.addEventListener('click', () => turnPage(1));
  root.querySelector('[data-reader-retry]').addEventListener('click', () => activeBook && loadBook(activeBook));
  root.querySelector('[data-zoom-out]').addEventListener('click', () => {
    zoom = Math.max(.65, Math.round((zoom - .15) * 100) / 100);
    zoomLabel.value = `${Math.round(zoom * 100)}%`;
    zoomLabel.textContent = zoomLabel.value;
    renderSpread();
  });
  root.querySelector('[data-zoom-in]').addEventListener('click', () => {
    zoom = Math.min(2, Math.round((zoom + .15) * 100) / 100);
    zoomLabel.value = `${Math.round(zoom * 100)}%`;
    zoomLabel.textContent = zoomLabel.value;
    renderSpread();
  });
  focusMode.addEventListener('click', () => {
    reader.classList.remove('is-toc-open');
    const enabled = reader.classList.toggle('is-focus-mode');
    focusMode.setAttribute('aria-pressed', String(enabled));
    focusMode.textContent = enabled ? 'Exit reading mode' : 'Reading mode';
    syncTocAccessibility();
    window.setTimeout(renderSpread, 40);
  });
  skipAnimation.addEventListener('click', () => {
    const enabled = !root.classList.contains('is-motionless');
    root.classList.toggle('is-motionless', enabled);
    skipAnimation.setAttribute('aria-pressed', String(enabled));
    try { window.localStorage.setItem('woteLivingLibraryMotionless', String(enabled)); } catch (_) { /* optional */ }
  });
  prefersReducedMotion.addEventListener?.('change', (event) => { if (event.matches) finishCinema(); });

  reader.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !paywall.hidden) {
      event.preventDefault();
      event.stopPropagation();
      hidePaywall();
      return;
    }
    if (!paywall.hidden) return;
    if (event.key === 'Escape' && readingInvitation.isOpen()) {
      event.preventDefault();
      event.stopPropagation();
      readingInvitation.hide();
      return;
    }
    if (cinemaLayer || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || event.target.matches('input,textarea,select,[contenteditable="true"]')) return;
    if (event.key === 'ArrowRight' || event.key === 'PageDown') { event.preventDefault(); turnPage(1); }
    if (event.key === 'ArrowLeft' || event.key === 'PageUp') { event.preventDefault(); turnPage(-1); }
    if (event.key === 'Home') { event.preventDefault(); currentPage = 1; renderSpread(); }
    if (event.key === 'End') { event.preventDefault(); currentPage = totalPages; renderSpread(); }
  });
  spread.addEventListener('pointerdown', (event) => {
    swipeStart = event.pointerType === 'touch' && !event.target.closest('a,button,.ll-pdf-text span') && window.getSelection()?.isCollapsed !== false
      ? { x: event.clientX, y: event.clientY } : null;
  });
  spread.addEventListener('pointerup', (event) => {
    if (swipeStart === null) return;
    const distance = event.clientX - swipeStart.x;
    const verticalDistance = Math.abs(event.clientY - swipeStart.y);
    swipeStart = null;
    if (window.getSelection()?.isCollapsed !== false && Math.abs(distance) > 55 && Math.abs(distance) > verticalDistance * 1.5) turnPage(distance < 0 ? 1 : -1);
  });
  spread.addEventListener('pointercancel', () => { swipeStart = null; });

  let resizeTimer;
  window.addEventListener('resize', () => {
    syncTocAccessibility();
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      if (reader.open && !closing && totalPages) renderSpread();
      else if (!busy && !closing && !reader.open) renderShelves();
    }, 140);
  });

  if ('ResizeObserver' in window) {
    const shelfObserver = new ResizeObserver(() => {
      window.clearTimeout(shelfResizeTimer);
      shelfResizeTimer = window.setTimeout(() => { if (!reader.open && !busy && !closing) renderShelves(); }, 120);
    });
    shelfObserver.observe(shelves);
  }

  const requestedBook = (() => {
    try { return new URL(window.location.href).searchParams.get('book'); } catch (_) { return null; }
  })();
  if (requestedBook) {
    const book = books.find((item) => item.id === requestedBook);
    const button = book && shelves.querySelector(`[data-book-id="${CSS.escape(book.id)}"]`);
    if (book && button) window.setTimeout(() => openBook(book, button), 120);
  }
}
